import { Controller } from "@hotwired/stimulus"
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnFrPackage from '@zxcvbn-ts/language-fr'

export default class extends Controller {
  static targets = ["input", "meter", "feedback"]

  connect() {
    this.update()
    this.submitButtons = []

    // Store original text immediately when controller connects
    this.storeOriginalButtonText()
  }

  storeOriginalButtonText() {
    const form = this.element
    const formId = form.id

    // Find all related submit buttons
    const buttonsInForm = Array.from(form.querySelectorAll('input[type="submit"], button[type="submit"]'))
    const buttonsOutsideForm = formId ?
      Array.from(document.querySelectorAll(`input[type="submit"][form="${formId}"], button[type="submit"][form="${formId}"]`)) : []

    const allButtons = [...buttonsInForm, ...buttonsOutsideForm]

    allButtons.forEach((button) => {
      if (button.tagName === 'INPUT') {
        if (!button.dataset.originalValue) {
          button.dataset.originalValue = button.value
        }
      } else {
        if (!button.dataset.originalText) {
          button.dataset.originalText = button.textContent
        }
      }
    })
  }

  beforeSubmit(event) {
    // Store reference to submit buttons before they get disabled
    const form = event.target
    const formId = form.id

    // Find buttons inside the form
    const buttonsInForm = Array.from(form.querySelectorAll('input[type="submit"], button[type="submit"]'))

    // Find buttons outside the form that reference this form via form attribute
    const buttonsOutsideForm = formId ?
      Array.from(document.querySelectorAll(`input[type="submit"][form="${formId}"], button[type="submit"][form="${formId}"]`)) : []

    this.submitButtons = [...buttonsInForm, ...buttonsOutsideForm]

    // Store original text/value before Turbo changes it
    this.submitButtons.forEach((button) => {
      if (button.tagName === 'INPUT') {
        button.dataset.originalValue = button.value
      } else {
        button.dataset.originalText = button.textContent
      }
    })
  }

  update() {
    try {
      const value = this.inputTarget.value
      const options = {
        translations: zxcvbnFrPackage.translations,
        graphs: zxcvbnCommonPackage.adjacencyGraphs,
        dictionary: {
          ...zxcvbnCommonPackage.dictionary,
          ...zxcvbnFrPackage.dictionary,
        },
      }

      zxcvbnOptions.setOptions(options)
      const result = zxcvbn(value)
      this.score = result.score // Store score for validation

      const colors = ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71", "#3498db"]
      const labels = ["Très faible", "Faible", "Moyen", "Bon", "Excellent"]

      this.feedbackTarget.innerHTML = result.feedback.warning || result.feedback.suggestions.join(" ") || " "

      this.meterTarget.innerHTML = `
        <div style="
          width: 100%;
          height: 10px;
          background: #eee;
          border-radius: 5px;
          overflow: hidden;
          margin-bottom: 0.5em;
        ">
          <div style="
            width: ${(result.score + 1) * 20}%;
            height: 100%;
            background: ${colors[result.score]};
            transition: width 0.3s;
          "></div>
        </div>
        <span style="color: ${colors[result.score]}; font-weight: bold;">
          ${labels[result.score]}
        </span>
      `
    } catch (error) {
      console.error("Password strength validation error:", error)
      this.score = 0 // Default to weak password on error
    }
  }

  validate(event) {
    if (this.score < 3) {
      event.preventDefault()
      alert("Le mot de passe doit être 'Bon' pour être accepté.")

      // Try multiple approaches to re-enable the button
      const form = event.target

      // Immediate attempt
      this.reEnableSubmitButtons(form)

      // Delayed attempts with increasing timeouts
      setTimeout(() => this.reEnableSubmitButtons(form), 10)
      setTimeout(() => this.reEnableSubmitButtons(form), 50)
      setTimeout(() => this.reEnableSubmitButtons(form), 100)
      setTimeout(() => this.reEnableSubmitButtons(form), 500)
    }
  }

  reEnableSubmitButtons(form) {
    // Use stored buttons if available, otherwise find them in and outside the form
    let submitButtons = this.submitButtons

    if (submitButtons.length === 0) {
      const formId = form.id
      const buttonsInForm = Array.from(form.querySelectorAll('input[type="submit"], button[type="submit"]'))
      const buttonsOutsideForm = formId ?
        Array.from(document.querySelectorAll(`input[type="submit"][form="${formId}"], button[type="submit"][form="${formId}"]`)) : []
      submitButtons = [...buttonsInForm, ...buttonsOutsideForm]
    }

    submitButtons.forEach((submitButton) => {
      // Force re-enable
      submitButton.disabled = false
      submitButton.removeAttribute('disabled')

      // Remove disabled classes
      submitButton.classList.remove('disabled', 'btn-disabled')

      // Reset styles
      submitButton.style.pointerEvents = 'auto'
      submitButton.style.opacity = '1'
      submitButton.style.cursor = 'pointer'

      // Restore original text/value
      if (submitButton.tagName === 'INPUT') {
        const originalValue = submitButton.dataset.originalValue
        if (originalValue) {
          submitButton.value = originalValue
        }
      } else {
        const originalText = submitButton.dataset.originalText
        if (originalText) {
          submitButton.textContent = originalText
        }
      }
    })
  }
}
